import React, { useContext, useEffect, useState } from 'react';
import { GraphUp, Search } from 'react-bootstrap-icons';
import { Navigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import MarketPage from '../../components/MobileNavigation/MarketPage';
import MarketOverviewPage from '../../components/MobileNavigation/MarketOverviewPage';
import MarketRelativeStrengthPage from '../../components/MobileNavigation/MarketRelativeStrengthPage';
import MarketPositionsPage from '../../components/MobileNavigation/MarketPositionsPage';
import DashboardPageHeader from '../../components/shared/DashboardPageHeader';
import ChartIntervalControl from '../../components/shared/ChartIntervalControl';
import useIsMobile from '../../hooks/useIsMobile';
import { getDashboardPageShellStyle } from '../../constants/dashboardLayout';
import { DASHBOARD_ROUTES } from '../../constants/dashboardRoutes';
import { canAccessMarketDataTools, isBrokerLinked } from '../../utils/brokerConnection';
import RelativeStrengthInfoButton from '../../components/MarketOverview/RelativeStrengthInfoButton';
import BrokerNotConnectedState from '../../components/shared/BrokerNotConnectedState';

const POSITION_MONITOR_INTERVAL_KEY = 'positionMonitorInterval';
const POSITION_MONITOR_CHART_TYPE_KEY = 'positionMonitorChartType';

function readChartType() {
  try {
    const stored = localStorage.getItem(POSITION_MONITOR_CHART_TYPE_KEY);
    if (stored === 'line' || stored === 'candle') return stored;
  } catch (_) {}
  return 'line';
}

function ChartTypeToggle({ value, onChange, colors }) {
  const options = [
    { id: 'line', label: 'Line chart', icon: <GraphUp size={14} /> },
    {
      id: 'candle',
      label: 'Candlestick chart',
      icon: (
        <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden>
          <path d="M5 1.5v3M5 10.5v4M11 1.5v2M11 9.5v5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <rect x="3.2" y="4.5" width="3.6" height="6" rx="0.6" fill="currentColor" />
          <rect x="9.2" y="3.5" width="3.6" height="6" rx="0.6" fill="currentColor" />
        </svg>
      ),
    },
  ];
  return (
    <div
      role="group"
      aria-label="Chart type"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
    >
      {options.map((option) => {
        const active = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            aria-label={option.label}
            aria-pressed={active}
            onClick={() => onChange(option.id)}
            style={{
              width: 28,
              height: 28,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              border: 'none',
              borderRadius: 6,
              background: active ? `${colors.primary}22` : 'transparent',
              color: active ? colors.primary : colors.textMuted,
              cursor: 'pointer',
            }}
          >
            {option.icon}
          </button>
        );
      })}
    </div>
  );
}

function readPositionMonitorInterval() {
  try {
    const parsed = JSON.parse(localStorage.getItem(POSITION_MONITOR_INTERVAL_KEY));
    if (Number.isFinite(parsed?.interval) && typeof parsed?.unit === 'string') {
      return parsed;
    }
  } catch (_) {}
  return { interval: 1, unit: 'Daily' };
}

/** Market Overview, Relative Strength, and Positions Overview inside DashboardShell. */
export default function DashboardMarketPage() {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const canUseMarketTools = canAccessMarketDataTools(user, selectedAccount);
  const brokerLinked = isBrokerLinked(user);
  const isPositions = location.pathname.includes('/market/positions');
  const isResearch = location.pathname.includes('/market/research');
  const isRelativeStrength = location.pathname.includes('/market/relative-strength');
  const [positionInterval, setPositionInterval] = useState(readPositionMonitorInterval);
  const [chartType, setChartType] = useState(readChartType);
  const [symbolQuery, setSymbolQuery] = useState('');
  const [rsMethodologyOpen, setRsMethodologyOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(POSITION_MONITOR_INTERVAL_KEY, JSON.stringify(positionInterval));
    } catch (_) {}
  }, [positionInterval]);

  useEffect(() => {
    try {
      localStorage.setItem(POSITION_MONITOR_CHART_TYPE_KEY, chartType);
    } catch (_) {}
  }, [chartType]);

  if (isMobile) {
    return <MarketPage />;
  }

  // /market/research is a permanent alias for the Research page, not a capability decision.
  if (isResearch) {
    return <Navigate to={DASHBOARD_ROUTES.research} replace />;
  }

  // Auth still hydrating — do not bounce to Research.
  if (!user) {
    return null;
  }

  if (!canUseMarketTools) {
    return <Navigate to={DASHBOARD_ROUTES.research} replace />;
  }

  return (
    <div
      style={{
        ...getDashboardPageShellStyle({ isMobile: false, colors }),
        ...(!isPositions
          ? {
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              padding: '36px 72px 24px',
            }
          : null),
      }}
    >
      <DashboardPageHeader
        title={
          isPositions
            ? 'Positions Overview'
            : isRelativeStrength
              ? 'Relative Strength'
              : 'Market Overview'
        }
        description={
          isPositions
            ? 'Open P&L, protection, and charts for each position'
            : isRelativeStrength
              ? 'How each symbol is performing versus the selected benchmark across timeframes.'
              : null
        }
        titleAdornment={isRelativeStrength ? (
          <RelativeStrengthInfoButton onClick={() => setRsMethodologyOpen(true)} />
        ) : null}
      />
      {isPositions ? (
        brokerLinked ? (
          <MarketPositionsPage
            interval={positionInterval.interval}
            unit={positionInterval.unit}
            symbolQuery={symbolQuery}
            chartType={chartType}
            toolbar={(
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                <label
                  style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    margin: 0,
                  }}
                >
                  <Search
                    size={13}
                    aria-hidden
                    style={{
                      position: 'absolute',
                      left: 10,
                      color: colors.textMuted,
                      pointerEvents: 'none',
                    }}
                  />
                  <input
                    type="search"
                    value={symbolQuery}
                    onChange={(event) => setSymbolQuery(event.target.value)}
                    placeholder="Search symbol or company"
                    aria-label="Search positions by symbol or company"
                    style={{
                      width: 210,
                      height: 32,
                      padding: '0 10px 0 30px',
                      border: `1px solid ${colors.border}`,
                      borderRadius: 8,
                      backgroundColor: colors.bgInput || colors.bgSecondary || colors.bgPrimary,
                      color: colors.text || colors.textMuted,
                      fontSize: 13,
                      outline: 'none',
                    }}
                  />
                </label>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ChartTypeToggle value={chartType} onChange={setChartType} colors={colors} />
                  <ChartIntervalControl
                    interval={positionInterval.interval}
                    unit={positionInterval.unit}
                    setIntervalAndUnit={(interval, unit) => setPositionInterval({ interval, unit })}
                    showSessionToggle={false}
                    drop="down"
                    align="end"
                  />
                </div>
              </div>
            )}
          />
        ) : (
          <BrokerNotConnectedState
            message="Connect your broker to see open P&L, protection, and charts for each position."
          />
        )
      ) : (
        <div style={{ flex: 1, minHeight: 0 }}>
          {isRelativeStrength ? (
            <MarketRelativeStrengthPage
              methodologyOpen={rsMethodologyOpen}
              onMethodologyOpenChange={setRsMethodologyOpen}
            />
          ) : (
            <MarketOverviewPage />
          )}
        </div>
      )}
    </div>
  );
}
